import { useEffect, useState } from 'react';

import { setSentryUser } from 'src/modules/app/components/Providers/Telemetry/Sentry';
import type { User, UserImpersonator } from 'src/shared/graphql/generated';
import { useProfileQuery } from 'src/shared/graphql/generated';
import { useSplit } from 'src/shared/hooks';
import { syncAppLocale } from 'src/shared/i18n/browserI18n';
import { identify } from 'src/shared/telemetry';

// import { useLocalStorageUser } from './useLocalStorageUser';

type useAuthParam = {
  readonly skipBranch?: boolean;
  skipProfile?: boolean;
  ssr?: boolean;
};

export function useAuthExperience({
  skipProfile = false,
  skipBranch = false,
  ssr = true,
}: useAuthParam = {}) {
  const [authed, setAuthed] = useState<boolean>(false);
  const { data, loading } = useProfileQuery({
    skip: skipProfile,
    ssr,
  });

  const profile = data?.profile;
  const user = profile?.user as User | undefined;

  // This function can trigger a full page reload if the user language is different from the current page language
  syncAppLocale(user);

  const impersonator = profile?.impersonator as UserImpersonator | null;

  const {
    updateUserId: updateSplitUserId,
    updateAttributes: updateSplitAttributes,
    client: splitClient,
  } = useSplit();

  // this is causing pages to reload when other pages that also use this are opened or reloaded: https://hoverinc.atlassian.net/browse/CONST-1249
  // useLocalStorageUser({ user, refresh: refetch });

  useEffect(() => {
    if (user) {
      const { email, id, intercomSha } = user;

      setSentryUser(user, impersonator);
      updateSplitUserId(id?.toString());
      updateSplitAttributes(user);

      void identify(
        {
          userId: id,
          email,
          intercomSha,
        },
        { skipBranch },
      );

      setAuthed(true);
    }
  }, [
    user,
    impersonator,
    skipBranch,
    updateSplitAttributes,
    updateSplitUserId,
  ]);

  return { authed, splitClient, loading, user };
}
